import {
  Box,
  Container,
  ExpandableSection,
  Grid,
  Header,
  SpaceBetween,
} from '@amzn/awsui-components-core';
import {
  ContentRequest,
  ContentRequestState,
  ContentRequestType,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import React from 'react';

import { withAppContext } from '../../contexts/AppContext';
import AppContextProps from '../../contexts/AppContextProps';
import ContentRequestActionButtons from './ContentRequestActionButtons';
import {
  getContentRequestTagLabels,
  getUserRoles,
  isBarRaiserForSuggestion,
  joinTagValues,
} from '../../api/UtilsApi';
import ContentRequestStatus from './ContentRequestStatus';
import ContentRequestAssignmentActions from './ContentRequestAssignmentActions';
import { convertEntities } from '@amzn/awscat-editor';

interface ContentRequestDetailsProps extends AppContextProps {
  contentRequest: ContentRequest;
  updateContentRequest: (
    contentRequestId: string,
    state: ContentRequestState,
    assigneeId?: string,
  ) => void;
  readonly?: boolean;
}

const ContentRequestDetails = ({
  contentRequest,
  appContext,
  updateContentRequest,
  readonly,
}: ContentRequestDetailsProps) => {
  // @ts-expect-error TS18048 - strict migration
  const { userId } = appContext.authnInfo.userProfile;
  const { entities } = appContext;
  const {
    contentRequestData,
    userName,
    createdDate,
    state,
    assigneeId,
    assigneeName,
    contentType,
    requestType,
  } = contentRequest;
  const { contentName, description, tags } = contentRequestData;
  const suggestionDescription =
    requestType === ContentRequestType.Update
      ? `Needs review as of `
      : `Suggested by ${userName} on `;
  const header = (
    <Header
      variant="h3"
      description={`${suggestionDescription} ${createdDate.toLocaleString(DateTime.DATE_FULL)}`}
    >
      {convertEntities(contentName, entities)}
    </Header>
  );
  const renderTagsInfo = () => {
    const tagLabels = getContentRequestTagLabels(tags);
    const joinedTags = joinTagValues(tagLabels);
    const tagsInfo = joinedTags.map((tag) => (
      <div key={tag.key + tag.value}>
        <ExpandableSection defaultExpanded header={tag.key}>
          <Box padding={{ left: 'l' }}>{tag.value}</Box>
        </ExpandableSection>
      </div>
    ));
    return tagsInfo;
  };

  const renderActionItems = () => {
    // @ts-expect-error TS18048 - strict migration
    const isBarRaiser = isBarRaiserForSuggestion(appContext.authnInfo.userProfile, contentRequest);
    if (readonly) {
      return null;
    }
    let actionButtons = null;
    if (assigneeId === userId) {
      actionButtons = (
        <ContentRequestActionButtons
          updateContentRequest={updateContentRequest}
          contentRequest={contentRequest}
        />
      );
    } else {
      // @ts-expect-error TS18048 - strict migration
      const userRoles = getUserRoles(appContext.authnInfo.userProfile);
      actionButtons = (
        <ContentRequestAssignmentActions
          contentRequest={contentRequest}
          handleSuggestionUpdate={updateContentRequest}
          userId={userId}
          isAdmin={userRoles.isAdmin}
          isBarRaiser={isBarRaiser}
          isTechWriter={userRoles.isTechWriter}
        />
      );
    }
    return (
      <Box margin={{ top: 'm' }}>
        <SpaceBetween direction="horizontal" size="xs">
          {actionButtons}
        </SpaceBetween>
      </Box>
    );
  };

  return (
    <div className="content-request-detail-container">
      <Grid gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
        <Container header={header}>
          {convertEntities(description, entities)}
          {renderActionItems()}
        </Container>
        <div className="content-request-detail-panel">
          <Header variant="h3">Details</Header>
          <ExpandableSection defaultExpanded header="Status">
            <Box padding={{ left: 'l' }}>
              <ContentRequestStatus contentRequestState={state} assigneeName={assigneeName} />
            </Box>
          </ExpandableSection>
          <ExpandableSection defaultExpanded header="Content type">
            <Box padding={{ left: 'l' }}>{contentType}</Box>
          </ExpandableSection>
          {renderTagsInfo()}
        </div>
      </Grid>
    </div>
  );
};

export default withAppContext(ContentRequestDetails);
